/*
 * TeamsOverview — the Teams tab landing page.
 *
 * Route: /app/tournaments/:eventId/teams
 *
 * Travis's mental model for the Teams tab: "show all of the team matches".
 * Each round in the event becomes a BroadcastRoundSection (per the
 * design handoff), so the user scrolls through the same broadcast
 * layout repeated per round. Hero scoreboards show that round's points
 * and the running event total. Roster + captain admin lives one click
 * away under "Manage rosters".
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Tv, Users } from 'lucide-react';

import { api } from '@/lib/api';
import { AppShell } from '@/components/AppShell';
import { BroadcastRoundSection, type SectionMatch, type SectionTeam } from '@/components/BroadcastRoundSection';
import { StablefordTeamsOverview } from '@/routes/tournaments/StablefordTeamsOverview';
import { paletteFor } from '@/lib/teamColors';
import { formatTeamCupPoints } from '@/lib/teamCupPoints';
import { useAuthStore } from '@/store/authStore';
import { useTournamentStore } from '@/store/tournamentStore';
import type { TeamStablefordPayload, TournamentPlayerStat } from '@golf-app/shared';

type MatchHole = {
  id: string;
  holeNumber: number;
  winnerTeamId: string | null;
  isHalved: boolean;
};

type Participant = {
  eventPlayerId: string;
  sideNumber: number;
  displayName: string;
  userId: string | null;
};

type RawMatch = {
  id: string;
  matchNumber: number;
  status: string;
  teamAId: string | null;
  teamBId: string | null;
  teamAPointsAwarded: number;
  teamBPointsAwarded: number;
  winningTeamId: string | null;
  finalResultDisplay: string | null;
  finalResultType: string | null;
};

type Round = {
  id: string;
  name: string;
  roundNumber: number;
  formatType: string;
  scoringMode: string;
  status: string;
  sessionType: string | null;
  courseNameSnapshot: string | null;
  defaultTeeNameSnapshot: string | null;
  matches: RawMatch[];
};

type EventPlayer = {
  id: string;
  displayName: string;
  teamId: string | null;
};

type Team = {
  id: string;
  name: string;
  primaryColor: string | null;
  captainEventPlayerId: string | null;
};

type EventBundle = {
  id: string;
  name: string;
  pointsNeeded: number | null;
  totalAvailablePoints: number | null;
  teams: Team[];
  players: EventPlayer[];
};

export default function TeamsOverview() {
  const { eventId } = useParams<{ eventId: string }>();
  const myUserId = useAuthStore((s) => s.userId);
  const { activeEvent, setActiveEvent } = useTournamentStore();

  const [event, setEvent] = useState<EventBundle | null>(null);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [matchesByRound, setMatchesByRound] = useState<Record<string, SectionMatch[]>>({});
  const [stablefordByRound, setStablefordByRound] = useState<Record<string, TeamStablefordPayload>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Player W/L/T records — absorbed from the retired Live Scoring page's
  // Stats tab. Loaded after the main content; failure just hides the
  // section (it's supplementary, not core).
  const [playerStats, setPlayerStats] = useState<TournamentPlayerStat[] | null>(null);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const ev = await api.getTournament(eventId) as any;
        if (cancelled) return;
        setActiveEvent(ev);
        setEvent(ev);

        const roundsList = await api.getTournamentRounds(eventId) as any;
        const rs: Round[] = Array.isArray(roundsList) ? roundsList : (roundsList.rounds ?? []);
        if (cancelled) return;

        const fullRounds: Round[] = await Promise.all(
          rs.map(async (r) => {
            const detail = await api.getTournamentRound(r.id) as any;
            return { ...r, matches: detail.matches ?? [] } as Round;
          }),
        );
        if (cancelled) return;
        setRounds(fullRounds);

        const stablefordEntries = await Promise.all(
          fullRounds
            .filter((round) => round.scoringMode === 'stableford')
            .map(async (round) => [round.id, await api.getTeamStableford(round.id)] as const),
        );
        if (cancelled) return;
        setStablefordByRound(Object.fromEntries(stablefordEntries));

        const allMatchIds = fullRounds
          .filter((round) => round.scoringMode !== 'stableford')
          .flatMap((r) => r.matches.map((m) => m.id));
        // Per-match catch keeps one flaky request from killing the page,
        // BUT we count failures: if every call failed, something
        // systemic broke (auth, permissions, network) and silently
        // rendering "TBD" everywhere hides it. That exact trap masked
        // a 403 permissions bug once already — never again.
        let scoringFailures = 0;
        const scorings = await Promise.all(
          allMatchIds.map((id) =>
            api.getMatchScoring(id)
              .then((s: any) => ({
                id,
                matchHoles: (s.matchHoles ?? []) as MatchHole[],
                participants: ((s.participants ?? []) as any[]).map((p) => ({
                  eventPlayerId: p.eventPlayerId,
                  sideNumber: p.sideNumber,
                  displayName: p.displayName,
                  userId: p.userId,
                } as Participant)),
              }))
              .catch(() => {
                scoringFailures++;
                return { id, matchHoles: [] as MatchHole[], participants: [] as Participant[] };
              }),
          ),
        );
        if (cancelled) return;
        if (allMatchIds.length > 0 && scoringFailures === allMatchIds.length) {
          throw new Error('Could not load match details. Pull to refresh or try again in a moment.');
        }
        const scoringMap = new Map(scorings.map((s) => [s.id, s]));

        const byRound: Record<string, SectionMatch[]> = {};
        for (const r of fullRounds) {
          byRound[r.id] = r.matches.map((m) => {
            const s = scoringMap.get(m.id);
            return {
              id: m.id,
              matchNumber: m.matchNumber,
              status: m.status,
              teamAId: m.teamAId,
              teamBId: m.teamBId,
              teamAPointsAwarded: m.teamAPointsAwarded ?? 0,
              teamBPointsAwarded: m.teamBPointsAwarded ?? 0,
              winningTeamId: m.winningTeamId,
              finalResultDisplay: m.finalResultDisplay,
              finalResultType: m.finalResultType,
              participants: s?.participants ?? [],
              matchHoles: s?.matchHoles ?? [],
            } as SectionMatch;
          });
        }
        setMatchesByRound(byRound);
        setError(null);
      } catch (e: any) {
        if (!cancelled) setError(e?.message ?? 'Failed to load team scoring');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, setActiveEvent]);

  const captainsByTeam = useMemo(() => {
    const m = new Map<string, string>();
    if (!event) return m;
    for (const t of event.teams) {
      if (!t.captainEventPlayerId) continue;
      const ep = event.players.find((p) => p.id === t.captainEventPlayerId);
      if (ep) m.set(t.id, firstName(ep.displayName));
    }
    return m;
  }, [event]);

  const teamA: SectionTeam | null = useMemo(() => {
    const t = event?.teams[0];
    if (!t) return null;
    return { id: t.id, name: t.name, primaryColor: t.primaryColor, captainFirstName: captainsByTeam.get(t.id) ?? null };
  }, [event, captainsByTeam]);

  const teamB: SectionTeam | null = useMemo(() => {
    const t = event?.teams[1];
    if (!t) return null;
    return { id: t.id, name: t.name, primaryColor: t.primaryColor, captainFirstName: captainsByTeam.get(t.id) ?? null };
  }, [event, captainsByTeam]);

  const eventTotals = useMemo(() => {
    let a = 0, b = 0;
    for (const r of rounds) {
      for (const m of r.matches) {
        a += m.teamAPointsAwarded ?? 0;
        b += m.teamBPointsAwarded ?? 0;
      }
    }
    return { teamA: a, teamB: b };
  }, [rounds]);

  const pointsToClinch = useMemo(() => {
    if (!event) return null;
    if (event.pointsNeeded != null) return Number(event.pointsNeeded);
    if (event.totalAvailablePoints != null) return Number(event.totalAvailablePoints) / 2 + 0.5;
    return null;
  }, [event]);

  const isManager = activeEvent?.myRole === 'owner' || activeEvent?.myRole === 'captain';
  const stablefordOnly = rounds.length > 0 && rounds.every((round) => round.scoringMode === 'stableford');

  // Lazy stats load — after the rounds render so the hero isn't blocked.
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    api.getEventStats(eventId)
      .then((res) => { if (!cancelled) setPlayerStats(res.players); })
      .catch(() => { /* supplementary section — hide on failure */ });
    return () => { cancelled = true; };
  }, [eventId]);

  if (loading) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }} fullBleed>
        <div className="t-loading"><div className="spinner" /></div>
      </AppShell>
    );
  }
  if (error) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }} fullBleed>
        <div className="t-empty">
          <div className="t-empty-title">Could not load team scoring</div>
          <div className="t-empty-hint">{error}</div>
        </div>
      </AppShell>
    );
  }

  if (stablefordOnly && eventId) {
    return (
      <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }} fullBleed>
        <StablefordTeamsOverview
          eventId={eventId}
          rounds={rounds}
          stablefordByRound={stablefordByRound}
          isManager={isManager}
        />
      </AppShell>
    );
  }

  return (
    <AppShell back={{ to: `/app/tournaments/${eventId}`, label: 'Event' }} fullBleed>
      <div style={{ background: 'var(--bcast-bg)', minHeight: '100%', color: 'var(--bcast-ink-1)' }}>
        {/* Action row: TV Mode for everyone (absorbed from the retired
            Live Scoring page — leave it running on the house TV), and
            roster admin for owner/captain. */}
        <div style={{ padding: '14px 28px 0', display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <Link
            to={`/app/tournaments/${eventId}/leaderboard/tv`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 14px',
              background: 'var(--bcast-card)',
              border: '1px solid var(--bcast-line)',
              borderRadius: 10,
              color: 'var(--bcast-ink-1)',
              fontSize: 13,
              fontWeight: 700,
              textDecoration: 'none',
            }}
          >
            <Tv width={14} height={14} /> TV Mode
          </Link>
          {isManager && (
            <Link
              to={`/app/tournaments/${eventId}/teams/manage`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 14px',
                background: 'var(--bcast-card)',
                border: '1px solid var(--bcast-line)',
                borderRadius: 10,
                color: 'var(--bcast-ink-1)',
                fontSize: 13,
                fontWeight: 700,
                textDecoration: 'none',
              }}
            >
              <Users width={14} height={14} /> Manage rosters
            </Link>
          )}
        </div>

        {/* Event-total scoreboard. Deliberately a different shape from
            the angled round banners below (centered, gold eyebrow) so
            "overall event score" vs "individual round" reads at a
            glance — Travis's note: the top block looked like just
            another round. */}
        {teamA && teamB && (
          <div className="bcast-event-total">
            <div className="bcast-event-total-eyebrow">Event Total</div>
            <div className="bcast-event-total-row">
              <span className="bcast-event-total-name" style={{ color: paletteFor(teamA.primaryColor, 1).solid }}>
                {teamA.name}
              </span>
              <span className="bcast-event-total-score bcast-tnum">{formatTeamCupPoints(eventTotals.teamA)}</span>
              <span className="bcast-event-total-dash">–</span>
              <span className="bcast-event-total-score bcast-tnum">{formatTeamCupPoints(eventTotals.teamB)}</span>
              <span className="bcast-event-total-name" style={{ color: paletteFor(teamB.primaryColor, 2).solid }}>
                {teamB.name}
              </span>
            </div>
          </div>
        )}

        {/* Clinch progress — absorbed from the retired Live Scoring page.
            One bar: Team A's points fill from the left, Team B's from the
            right, both racing toward the centerline-ish clinch number. */}
        {pointsToClinch != null && teamA && teamB && (
          <ClinchBar
            a={eventTotals.teamA}
            b={eventTotals.teamB}
            teamA={teamA}
            teamB={teamB}
            pointsNeeded={pointsToClinch}
            totalAvailable={event?.totalAvailablePoints != null ? Number(event.totalAvailablePoints) : null}
          />
        )}

        {rounds.length === 0 && (
          <div style={{ padding: '60px 28px', textAlign: 'center', color: 'var(--bcast-ink-2)' }}>
            No rounds yet. Add one to start scoring.
          </div>
        )}

        {/* One BroadcastRoundSection per round, each headed by a
            ROUND N · name tag so the per-round blocks can't be
            mistaken for the event total above. */}
        {rounds.map((r, idx) => (
          <div key={r.id} style={{ marginTop: idx === 0 ? 20 : 28 }}>
            <div className="bcast-round-header">
              <span className="bcast-round-header-num">ROUND {r.roundNumber}</span>
              <span className="bcast-round-header-name">{r.name}</span>
            </div>
            <BroadcastRoundSection
              round={{
                id: r.id,
                name: r.name,
                roundNumber: r.roundNumber,
                totalRoundsInEvent: rounds.length,
                courseNameSnapshot: r.courseNameSnapshot,
                defaultTeeNameSnapshot: r.defaultTeeNameSnapshot,
                parTotal: null,
                sessionType: r.sessionType,
                formatType: r.formatType,
              }}
              teamA={teamA}
              teamB={teamB}
              matches={matchesByRound[r.id] ?? []}
              eventTotals={eventTotals}
              pointsToClinch={pointsToClinch}
              myUserId={myUserId}
            />
          </div>
        ))}

        {/* Player records — absorbed from the retired Live Scoring page's
            Stats tab. Only renders once at least one match is final. */}
        {playerStats && playerStats.length > 0 && teamA && teamB && (
          <PlayerRecords stats={playerStats} teamA={teamA} teamB={teamB} />
        )}

        <div style={{ padding: '40px 28px' }} />
      </div>
    </AppShell>
  );
}

// ── ClinchBar ─────────────────────────────────────────────────────────────
// Team A fills from the left, Team B from the right, racing toward the
// clinch threshold. The label spells out the target so nobody has to do
// Team match-play math after a few beers.
function ClinchBar({
  a, b, teamA, teamB, pointsNeeded, totalAvailable,
}: {
  a: number;
  b: number;
  teamA: SectionTeam;
  teamB: SectionTeam;
  pointsNeeded: number;
  totalAvailable: number | null;
}) {
  const total = totalAvailable != null && totalAvailable > 0
    ? totalAvailable
    : Math.max(pointsNeeded * 2 - 1, a + b, 1);
  const aPct = Math.min(100, (a / total) * 100);
  const bPct = Math.min(100 - aPct, (b / total) * 100);
  const pa = paletteFor(teamA.primaryColor, 1);
  const pb = paletteFor(teamB.primaryColor, 2);
  return (
    <div style={{ padding: '16px 28px 0' }}>
      <div
        style={{
          height: 10,
          borderRadius: 5,
          background: 'var(--bcast-card-2)',
          overflow: 'hidden',
          display: 'flex',
          border: '1px solid var(--bcast-line)',
        }}
      >
        <div style={{ width: `${aPct}%`, background: pa.solid, transition: 'width 300ms ease' }} />
        <div style={{ flex: 1 }} />
        <div style={{ width: `${bPct}%`, background: pb.solid, transition: 'width 300ms ease' }} />
      </div>
      <div
        style={{
          textAlign: 'center',
          marginTop: 8,
          fontFamily: 'var(--bcast-condensed)',
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '0.12em',
          color: 'var(--bcast-ink-3)',
        }}
      >
        FIRST TO {formatTeamCupPoints(pointsNeeded)} · MOST POINTS WINS
      </div>
    </div>
  );
}

// ── PlayerRecords ─────────────────────────────────────────────────────────
function PlayerRecords({
  stats, teamA, teamB,
}: {
  stats: TournamentPlayerStat[];
  teamA: SectionTeam;
  teamB: SectionTeam;
}) {
  const pa = paletteFor(teamA.primaryColor, 1);
  const pb = paletteFor(teamB.primaryColor, 2);
  const dotColor = (teamId: string | null) =>
    teamId === teamA.id ? pa.solid : teamId === teamB.id ? pb.solid : 'var(--bcast-ink-3)';
  return (
    <section style={{ padding: '28px 28px 0' }}>
      <div
        style={{
          fontFamily: 'var(--bcast-condensed)',
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: '0.14em',
          color: 'var(--bcast-ink-3)',
          marginBottom: 10,
        }}
      >
        PLAYER RECORDS
      </div>
      <div
        style={{
          background: 'var(--bcast-card)',
          border: '1px solid var(--bcast-line)',
          borderRadius: 14,
          overflow: 'hidden',
        }}
      >
        {stats.map((p, i) => (
          <div
            key={p.eventPlayerId}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderTop: i > 0 ? '1px solid var(--bcast-line)' : undefined,
            }}
          >
            <span
              className="bcast-tnum"
              style={{ width: 22, fontSize: 12, fontWeight: 800, color: 'var(--bcast-ink-3)', flexShrink: 0 }}
            >
              {i + 1}
            </span>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor(p.teamId), flexShrink: 0 }} />
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--bcast-ink-1)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {p.displayName}
            </span>
            <span className="bcast-tnum" style={{ fontSize: 13, fontWeight: 700, color: 'var(--bcast-ink-2)', flexShrink: 0 }}>
              {p.wins}-{p.losses}-{p.ties}
            </span>
            <span
              className="bcast-tnum"
              style={{ width: 64, textAlign: 'right', fontSize: 13, fontWeight: 800, color: 'var(--bcast-ink-1)', flexShrink: 0 }}
            >
              {formatTeamCupPoints(p.pointsEarned)} PTS
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function firstName(s: string): string {
  return (s ?? '').trim().split(/\s+/)[0] || s;
}
